import {
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components/native';

import {
  RouteProp,
  StackActions,
  useFocusEffect,
  useNavigation,
  useRoute
} from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';

import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import Button from '../../components/Button';
import { H5 } from '../../components/Typography';
import DynamicForm, {
  DynamicFormBackApi
} from '../../dynamic-forms/DynamicForm';
import {
  useFormNavigation,
  useRegisterFormBack
} from '../../dynamic-forms/navigation/FormNavigationContext';
import {
  appendSectionHistory,
  parseSectionHistory
} from '../../dynamic-forms/navigation/formBack';
import DynamicFormProvider from '../../dynamic-forms/DynamicFormProvider';
import { useGetFormTemplate } from '../../dynamic-forms/hooks/useGetFormTemplate';
import { DrawerParamList } from '../../navigation/DrawerNavigator';
import Spacer from '../../components/Spacer';
import FormDataSource from '../../dynamic-forms/FormDataSource';
import { mergeDraftValues } from '../../dynamic-forms/drafts/formDraftStore';
import { useFormDraft } from '../../dynamic-forms/drafts/useFormDraft';
import { useRunQuery } from '../../dynamic-forms/hooks/useRunQuery';
import { useUserContext } from '../../context/UserContext';
import { camelCase } from 'lodash';
import { FormElementMeta } from '@people-science/forms-core';
import AlertDialog from '../../components/AlertDialog';

const StyledFormNotFoundContainerView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

/**
 * Renders the dynamic form template created from the Chloe Admin Portal. The
 * following route.params are required `formTemplateId` or `formTemplateName`.
 * Provide a `entityDataId` to edit an existing entity; otherwise, a new entity
 * data record will be created.
 */
const FormTemplateScreen: React.FC<{ formTemplateName?: string }> = props => {
  const { user } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route = useRoute<RouteProp<DrawerParamList, 'FormTemplate'>>();
  const {
    formTemplateId,
    formTemplateName = props.formTemplateName,
    studyParticipantId,
    path
  } = route.params || {};
  const entityDataIdRef = useRef(route.params?.entityDataId);
  entityDataIdRef.current = route.params?.entityDataId;
  const history = useMemo(
    () => parseSectionHistory(route.params?.history),
    [route.params?.history]
  );
  // fields to focus on and complete
  let dataSourceFields: string[] = useMemo(
    () =>
      route.params?.dataSourceFields
        ? JSON.parse(route.params.dataSourceFields)
        : [],
    [route.params?.dataSourceFields]
  );
  const { formTemplate, loadingFormTemplate, getFormTemplate } =
    useGetFormTemplate(formTemplateId || formTemplateName || '');

  const statePath = useMemo(() => {
    const stateNodes = formTemplate?.config?.states;
    if (dataSourceFields.length === 0 || !stateNodes) return path;
    for (const stateNode of stateNodes) {
      if (!stateNode.meta?.elements) continue;
      if (
        stateNode.meta.elements.some(e =>
          dataSourceFields.includes(e.dataSource || '')
        )
      ) {
        return `/${formTemplate.config?.name}/${stateNode.name}`;
      }
    }
    return path;
  }, [dataSourceFields, formTemplate?.config, path]);
  // Load the participant event form data on mount as well as refresh the data
  // when the screen comes into focus from the route navigation via
  // useFocusEffect to prevent display of previous state.
  useFocusEffect(
    useCallback(() => {
      if (formTemplateId || formTemplateName) getFormTemplate();
    }, [getFormTemplate, formTemplateId, formTemplateName])
  );

  // run query to get entity data
  const { queryData, isRunningQuery, runQueryGetOne } = useRunQuery();
  useFocusEffect(
    useCallback(() => {
      if (entityDataIdRef.current && user?.id && formTemplate?.entity) {
        const entitySchema = formTemplate?.entity;
        const alias = camelCase(entitySchema.name);
        // an entityDataId in route params outlives a change of formTemplateId,
        // so unscoped it can address another participant's row in this table
        const ownerScoped =
          studyParticipantId &&
          entitySchema.columns?.some(
            (column: any) => column.name === 'studyParticipantId'
          );
        runQueryGetOne({
          queryType: 'select',
          selects: [],
          mainAlias: {
            type: 'from',
            name: alias,
            tablePath: entitySchema.tableName
          },
          wheres: [
            {
              type: 'simple',
              condition: {
                operator: 'equal',
                parameters: [`${alias}.id`, entityDataIdRef.current.toString()]
              }
            },
            ...(ownerScoped
              ? [
                  {
                    type: 'simple' as const,
                    condition: {
                      operator: 'equal' as const,
                      parameters: [
                        `${alias}.studyParticipantId`,
                        studyParticipantId.toString()
                      ]
                    }
                  }
                ]
              : [])
          ],
          orderBys: [{ propertyPath: `${alias}.created`, order: 'DESC' }]
        });
      }
    }, [user?.id, formTemplate?.entity, runQueryGetOne, studyParticipantId])
  );

  // load existing data if any
  const [openConflict, setOpenConflict] = useState(false);
  const [formValues, setFormValues] = useState(queryData);
  useEffect(() => {
    if (queryData) {
      setFormValues(queryData);
    } else {
      setFormValues({});
    }
  }, [queryData]);

  // Local draft (CD-222): survive an app kill mid-section. Restored on a
  // fresh open only (mid-walk section pushes carry `path`); the server copy
  // always wins per field.
  const draft = useFormDraft({
    userId: user?.id,
    formTemplateId: formTemplate?.id,
    instanceId: route.params?.entityDataId,
    formTemplateVersion: formTemplate?.version,
    formConfig: formTemplate?.config
  });
  const [restoredStatePath, setRestoredStatePath] = useState<string>();
  const [restoredHistory, setRestoredHistory] = useState<string[]>();
  const [draftChecked, setDraftChecked] = useState(false);
  useEffect(() => {
    if (draftChecked || !formTemplate) return;
    if (!draft.enabled || path) {
      setDraftChecked(true);
      return;
    }
    let stale = false;
    draft.restoreDraft().then(payload => {
      if (stale) return;
      if (payload) {
        setFormValues(current =>
          mergeDraftValues(current || {}, payload.formValues)
        );
        if (payload.history) setRestoredHistory(payload.history);
        if (payload.statePath) {
          setRestoredStatePath(payload.statePath);
          // seed the writer so later value-only writes keep the resume point
          draft.saveDraftStatePath(payload.statePath, payload.history);
        }
      }
      setDraftChecked(true);
    });
    return () => {
      stale = true;
    };
  }, [draft, draftChecked, formTemplate, path]);
  // mid-walk sections seed their own path so drafts resume where typed
  useEffect(() => {
    if (draft.enabled && path) draft.saveDraftStatePath(path, history);
  }, [draft, path, history]);
  const backApiRef = useRef<DynamicFormBackApi | undefined>(undefined);
  const { confirmLeave } = useFormNavigation();
  const currentHistory = restoredHistory ?? history;
  const currentPath =
    statePath ??
    restoredStatePath ??
    (formTemplate?.config?.states?.[0]
      ? `/${formTemplate.config.name}/${formTemplate.config.states[0].name}`
      : undefined);
  useFocusEffect(
    useCallback(() => {
      if (!backApiRef.current) return;
      backApiRef.current.syncToPath(statePath ?? restoredStatePath);
      if (draft.enabled && currentPath) {
        draft.saveDraftStatePath(currentPath, currentHistory);
      }
    }, [statePath, restoredStatePath, draft, currentPath, currentHistory])
  );
  useRegisterFormBack(async () => {
    const state = navigation.getState();
    const previous = state.routes[state.index - 1];
    const previousParams = (previous?.params || {}) as {
      formTemplateId?: number | string;
      formTemplateName?: string;
    };
    const sameTemplate =
      previousParams.formTemplateId !== undefined
        ? String(previousParams.formTemplateId) === String(formTemplate?.id)
        : previousParams.formTemplateName !== undefined &&
          previousParams.formTemplateName === formTemplateName;
    if (previous?.name === route.name && sameTemplate) return false;
    const step = backApiRef.current?.goBack();
    if (step) {
      setRestoredHistory(step.history);
      navigation.setParams({
        path: step.path,
        history: JSON.stringify(step.history)
      });
      return true;
    }
    if (backApiRef.current?.hasUnsavedAnswers()) {
      return !(await confirmLeave());
    }
    return false;
  });

  // display spinner while loading elements to prevent flickering
  const [loadingElements, setLoadingElements] = useState(false);

  // readyToDisplay: if editing, wait for data to load (needed for
  // AssignStudyTemplatePlugin); the draft check gates rendering so a restored
  // statePath applies before the form mounts. It must not gate the
  // no-template path — that would trap a failed template load on the spinner
  // instead of the Form Not Found fallback.
  const readyToDisplay = useMemo(
    () =>
      (formTemplate ? draftChecked : true) &&
      (entityDataIdRef.current ? Boolean(queryData && formValues) : true),
    [draftChecked, formTemplate, formValues, queryData]
  );
  return !readyToDisplay || isRunningQuery || loadingFormTemplate ? (
    <ActivityIndicatorScreen />
  ) : (!formTemplateId && !formTemplateName) || !formTemplate ? (
    <StyledFormNotFoundContainerView>
      <H5 color='light'>Form Not Found</H5>
      <Spacer size={3} />
      <Button
        outlined
        size='small'
        onPress={() => {
          navigation.goBack();
        }}
      >
        Back
      </Button>
    </StyledFormNotFoundContainerView>
  ) : (
    <DynamicFormProvider
      entitySchema={formTemplate?.entity}
      formStateNode={formTemplate?.config}
      onLoadProgress={loadingElementQueue => {
        setLoadingElements(loadingElementQueue.length > 0);
      }}
      onChange={values => {
        draft.saveDraftValues(values);
      }}
    >
      <FormDataSource
        formTemplate={formTemplate}
        formData={formValues}
        onSubmitted={isFinal => {
          if (isFinal) draft.clearDraft();
        }}
        onCreated={entity => {
          // NOTE: this might be problematic; need to test and compare with
          // StudyEventFormScreen where the entity creation would cause state
          // conflict as this fires along with onStatePathChange (below).
          // See fix on StudyEventFormScreen and test.
          entityDataIdRef.current = entity.id;
          const params = { entityDataId: entity.id } as any;
          if (entity.studyParticipantId)
            params.studyParticipantId = entity.studyParticipantId;
          navigation.setParams(params);
        }}
        onError={error => {
          if (error.statusCode === 409) {
            setOpenConflict(true);
          }
        }}
      >
        <DynamicForm
          values={formValues}
          statePath={statePath ?? restoredStatePath}
          restoredHistory={currentHistory}
          savedValues={queryData}
          backApiRef={backApiRef}
          onChange={formValues => {
            // setFormValues(formValues as any);
            // console.log('onChange formValues', formValues);
            // const keys = Object.keys(formValues);
          }}
          onBack={() => {
            navigation.pop();
          }}
          onStatePathChange={(
            path,
            stateNode,
            sectionIndex,
            sectionCount,
            stateMachine
          ) => {
            // logic checks if passes in (route params) dataSourceFields are
            // specified where only those dataSourceField related questions are
            // asekd to the user before redirecting them back to the previous
            // screen. This is needed in the StudyOverviewCard.
            const nextHistory = appendSectionHistory(
              currentHistory,
              currentPath
            );
            // write-through so a kill during the transition resumes here
            draft.saveDraftStatePath(path, nextHistory);
            if (sectionIndex < sectionCount) {
              if (statePath && dataSourceFields.length > 0) {
                const previousStateNode = stateMachine.getStateNode(statePath);
                dataSourceFields = dataSourceFields.filter(d => {
                  if (
                    previousStateNode?.meta?.elements?.some(
                      (e: FormElementMeta<any, any>) => e.dataSource === d
                    )
                  ) {
                    return false; // remove data source field if in stateNode
                  }
                  return true;
                });
                if (dataSourceFields.length === 0) {
                  // navigate to previous screen after completing all
                  // dataSourceFields related questions
                  const navigationState = navigation.getState();
                  let popCount = 0;
                  for (let i = navigationState.routes.length - 1; i >= 0; i--) {
                    if (navigationState.routes[i].name !== 'FormTemplate') {
                      navigation.pop(popCount);
                      return;
                    }
                    popCount++;
                  }
                }
              }
              const newSectionIndex = sectionIndex + 1;
              navigation.push('FormTemplate', {
                path,
                entityDataId: entityDataIdRef.current,
                sectionIndex: newSectionIndex,
                formTemplateId,
                formTemplateName,
                studyParticipantId:
                  studyParticipantId || queryData?.studyParticipantId,
                history: JSON.stringify(nextHistory)
                // dataSourceFields: JSON.stringify(dataSourceFields)
              });
            } else if (sectionIndex >= sectionCount) {
              // NOTE: technically this would never occur (you can't configure
              // a form transition past the last section)
              navigation.dispatch(
                StackActions.replace('DrawerNavigator', {
                  screen: 'BottomTabNav'
                })
              );
            }
          }}
        />
      </FormDataSource>
      {loadingElements && (
        <ActivityIndicatorScreen modal backgroundColor='#fff' />
      )}
      {openConflict && (
        <AlertDialog
          title='Already in a Study'
          message='Oops! Looks like you already have a study in progress. To support better science, you are limited to one study at a time. The more things you study together, the harder it is to determine what may be leading to changes.'
          onCloseClicked={() => {
            setOpenConflict(false);
            navigation.dispatch(
              StackActions.replace('DrawerNavigator', {
                screen: 'BottomTabNav',
                params: {
                  screen: 'StudiesNav'
                }
              })
            );
          }}
        />
      )}
    </DynamicFormProvider>
  );
};

export default FormTemplateScreen;
